import React from 'react'
import dateFns from 'date-fns'

import { Text, Select, Toggle, Button } from 'components/base'
import { IncentiveBoxLayout } from 'components/Utils/IncentiveBox/Layout'

const DEFAULT_BACKUP_VALUE = 'weekly'
const AUTOMATED_BACKUP_VALUES = [
  {
    title: 'Daily',
    value: 'daily',
    description: 'Receive a daily backup',
  },
  {
    title: 'Weekly',
    value: 'weekly',
    description: 'Receive a weekly backup',
  },
  {
    title: 'Monthly',
    value: 'monthly',
    description: 'Receive a monthly backup',
  },
]

type BackupProps = {
  organizationOwnerEmail: string
  organizationRequestedManualBackupAt: string | null
  organizationRequestedManualBackupByName: string | null
  organizationAutomatedBackupSchedule: string | null
  organizationIsFree: boolean
  canManage: boolean
  isOwner: boolean
  updateOrganization: (params: any) => void
  requestManualOrganizationBackup: (params: any) => void
  goToOrganizationUpgrade: (params: any) => void
}

export default function BackupLayout({
  organizationIsFree,
  organizationOwnerEmail,
  organizationRequestedManualBackupByName,
  organizationRequestedManualBackupAt,
  organizationAutomatedBackupSchedule,
  canManage,
  isOwner,
  updateOrganization,
  requestManualOrganizationBackup,
  goToOrganizationUpgrade,
}: BackupProps) {
  const onChangeAutomatedBackupStatus = () => {
    updateOrganization({
      automatedBackupSchedule: organizationAutomatedBackupSchedule
        ? null
        : DEFAULT_BACKUP_VALUE,
    })
  }

  const onChangeAutomatedBackupSchedule = (value: string) => {
    updateOrganization({
      automatedBackupSchedule: value,
    })
  }

  const getSelectedAutomatedBackupValue = () => {
    const optionSelected = AUTOMATED_BACKUP_VALUES.find(
      option => option.value === organizationAutomatedBackupSchedule
    )
    return (optionSelected && optionSelected.value) || DEFAULT_BACKUP_VALUE
  }

  return (
    <div className="flexbox flexbox-space-between flexbox-column">
      <Text.Corpus className="padding-top-small">
        {` Safely back up your data as a ZIP file containing all of your Slite content as markdown. The backup ZIP will be emailed to team owners `}
        <span className="font-weight-bold">{`(${organizationOwnerEmail})`}</span>
        {`. Please note that files uploaded to docs are not currently available in backups`}
      </Text.Corpus>
      <div className="margin-top-medium">
        <Button.Secondary
          onClick={requestManualOrganizationBackup}
          disabled={!canManage}
        >
          Back up now
        </Button.Secondary>
        {!canManage && (
          <Text.Detail className="font-weight-bold padding-top-small">
            {`This function is only available for the team owner.`}
          </Text.Detail>
        )}
        {canManage &&
          organizationRequestedManualBackupAt && (
            <Text.Detail className="padding-top-small">
              {`Latest manual backup requested on ${dateFns.format(
                organizationRequestedManualBackupAt,
                'DD/MM/YYYY HH:mm'
              )} by ${organizationRequestedManualBackupByName}`}
            </Text.Detail>
          )}
      </div>
      {organizationIsFree && (
        <IncentiveBoxLayout
          incentiveId="AutomaticBackup"
          className="margin-top-medium"
          goToOrganizationUpgrade={goToOrganizationUpgrade}
        />
      )}
      {!organizationIsFree && (
        <div className="flexbox flexbox-align-center margin-top-medium">
          <Text.Section className="margin-right-medium">
            Automatic backups
          </Text.Section>
          <Toggle
            disabled={organizationIsFree}
            label="Automated backup"
            className="margin-right-medium"
            checked={organizationAutomatedBackupSchedule}
            onChange={onChangeAutomatedBackupStatus}
          />
          <div className="flexbox flexbox-align-center">
            <Select
              id="automatedBackupSchedule"
              bodyProps={
                !isOwner && {
                  placement: 'custom',
                  placementCustomStyle: {
                    bottom: 45,
                  },
                }
              }
              disabled={
                organizationIsFree || !organizationAutomatedBackupSchedule
              }
              value={getSelectedAutomatedBackupValue()}
              onChange={onChangeAutomatedBackupSchedule}
              options={AUTOMATED_BACKUP_VALUES}
            />
          </div>
        </div>
      )}
    </div>
  )
}
